import type { HTMLAttributes, JSX, ReactNode } from 'react';
import clsx from 'clsx';

/**
 * Card primitives mirroring the Agent Studio ui/card.tsx contract:
 * rounded-xl border + raised background + subtle shadow.
 * Sub-components are exposed the same way (Card / CardHeader /
 * CardTitle / CardDescription / CardContent / CardFooter) so pages
 * can be ported almost verbatim between the two apps.
 */

type DivProps = HTMLAttributes<HTMLDivElement>;

export function Card({ className, children, ...rest }: DivProps): JSX.Element {
  return (
    <div className={clsx('ui-card', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({ className, children, ...rest }: DivProps): JSX.Element {
  return (
    <div className={clsx('ui-card__header', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardTitle({
  className, children, ...rest
}: HTMLAttributes<HTMLHeadingElement>): JSX.Element {
  return (
    <h3 className={clsx('ui-card__title', className)} {...rest}>
      {children}
    </h3>
  );
}

export function CardDescription({
  className, children, ...rest
}: HTMLAttributes<HTMLParagraphElement>): JSX.Element {
  return (
    <p className={clsx('ui-card__description', className)} {...rest}>
      {children}
    </p>
  );
}

export function CardContent({ className, children, ...rest }: DivProps): JSX.Element {
  return (
    <div className={clsx('ui-card__content', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ className, children, ...rest }: DivProps): JSX.Element {
  return (
    <div className={clsx('ui-card__footer', className)} {...rest}>
      {children}
    </div>
  );
}

export type { ReactNode };
